/**
 * ============================================================================
 * STORE PERFORMANCE UTILITIES
 * ============================================================================
 * 
 * Performance optimization utilities for stores
 */

/**
 * Maximum array size in store
 */
export const MAX_ARRAY_SIZE = 1000;

/**
 * Limit array size
 * @param {Array} array - Array to limit
 * @param {number} maxSize - Maximum size (default: MAX_ARRAY_SIZE)
 * @returns {Array} Limited array
 */
export const limitArraySize = (array, maxSize = MAX_ARRAY_SIZE) => {
  if (!Array.isArray(array)) return array;
  if (array.length <= maxSize) return array;
  return array.slice(0, maxSize);
};

/**
 * Deduplicate array by ID
 * @param {Array} array - Array to deduplicate
 * @param {string} idKey - ID key (default: 'id')
 * @returns {Array} Deduplicated array
 */
export const deduplicateById = (array, idKey = 'id') => {
  if (!Array.isArray(array)) return array;
  const seen = new Set();
  return array.filter((item) => {
    const id = item?.[idKey];
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Add item with deduplication and size limit
 * @param {Array} currentArray - Current array
 * @param {*} newItem - New item to add
 * @param {Object} options - Options
 * @param {string} options.idKey - ID key (default: 'id')
 * @param {number} options.maxSize - Maximum size (default: MAX_ARRAY_SIZE)
 * @param {boolean} options.prepend - Prepend instead of append (default: true)
 * @returns {Array} Updated array
 */
export const addItemOptimized = (currentArray, newItem, options = {}) => {
  const {
    idKey = 'id',
    maxSize = MAX_ARRAY_SIZE,
    prepend = true,
  } = options;

  if (!Array.isArray(currentArray)) return [newItem];

  // Deduplicate
  const deduplicated = deduplicateById(currentArray, idKey);

  // Add new item
  const updated = prepend
    ? [newItem, ...deduplicated]
    : [...deduplicated, newItem];

  // Limit size
  return limitArraySize(updated, maxSize);
};

/**
 * Update item in array
 * @param {Array} array - Array to update
 * @param {string} id - Item ID
 * @param {Object} updates - Updates to apply
 * @param {string} idKey - ID key (default: 'id')
 * @returns {Array} Updated array
 */
export const updateItemInArray = (array, id, updates, idKey = 'id') => {
  if (!Array.isArray(array)) return array;
  return array.map((item) =>
    item?.[idKey] === id ? { ...item, ...updates } : item
  );
};

/**
 * Remove item from array
 * @param {Array} array - Array to update
 * @param {string} id - Item ID
 * @param {string} idKey - ID key (default: 'id')
 * @returns {Array} Updated array
 */
export const removeItemFromArray = (array, id, idKey = 'id') => {
  if (!Array.isArray(array)) return array;
  return array.filter((item) => item?.[idKey] !== id);
};

/**
 * Find item in array
 * @param {Array} array - Array to search
 * @param {string} id - Item ID
 * @param {string} idKey - ID key (default: 'id')
 * @returns {*} Found item or null
 */
export const findItemInArray = (array, id, idKey = 'id') => {
  if (!Array.isArray(array)) return null;
  return array.find((item) => item?.[idKey] === id) || null;
};
